import { useState } from "react";
import { usePortailAuth } from "../lib/auth-context";
import { usePortailRouter, PortailLink } from "../lib/router-context";
import {
  LayoutDashboard, Building2, FileText, ShoppingCart, Bot,
  Package, Wrench, FileSignature, LifeBuoy, LogOut, ChevronLeft, ChevronRight, BookOpen, MessageCircle, Briefcase, Bell, Lightbulb, Settings,
} from "lucide-react";
import { PortailFeedbackModal } from "./PortailFeedbackModal";
import { PortailNotificationsPanel } from "./PortailNotificationsPanel";

function getInitials(prenom: string | null, nom: string | null) {
  const p = prenom?.[0] ?? "";
  const n = nom?.[0] ?? "";
  return (p + n).toUpperCase() || "?";
}

interface PortailLayoutProps {
  children: React.ReactNode;
}

export function PortailLayout({ children }: PortailLayoutProps) {
  const { portailUser, brand, logout } = usePortailAuth();
  const { path, unreadDocCount, unreadNotifCount, refreshNotifCount, navigate } = usePortailRouter();
  const [collapsed, setCollapsed] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [notifPanelOpen, setNotifPanelOpen] = useState(false);

  const primary = brand?.couleurPrimaire || "#2563eb";
  const logo = brand?.logoUrl;
  const brandName = brand?.companyName || "Partenaire";

  const isActive = (href: string) => {
    if (href === "/") return path === "/" || path === "";
    return path.startsWith(href);
  };

  const NAV_ITEMS = [
    { label: "Tableau de bord", icon: LayoutDashboard, href: "/" },
    { label: "Profil société", icon: Building2, href: "/societe" },
    { label: "Documents", icon: FileText, href: "/documents", badge: 0 },
    { label: "Ressources", icon: BookOpen, href: "/ressources", badge: unreadDocCount > 0 ? unreadDocCount : 0 },
    { label: "Commandes", icon: ShoppingCart, href: "/commandes" },
    { label: "Robots actifs", icon: Bot, href: "/robots" },
    { label: "Catalogue robots", icon: Package, href: "/catalogue" },
    { label: "Pièces détachées", icon: Wrench, href: "/pieces" },
    { label: "Devis", icon: FileSignature, href: "/devis" },
    { label: "Sous-traitance", icon: Briefcase, href: "/prestations", badge: unreadNotifCount > 0 ? unreadNotifCount : 0 },
    { label: "Chats SAV", icon: MessageCircle, href: "/chats" },
    { label: "Tickets SAV", icon: LifeBuoy, href: "/tickets" },
  ];

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Sidebar */}
      <aside
        className={`flex flex-col shrink-0 border-r border-border/60 bg-card transition-all duration-200 ${collapsed ? "w-16" : "w-60"}`}
      >
        {/* Header marque */}
        <div className="flex items-center gap-3 px-3 py-4 border-b border-border/40 min-h-[64px]">
          {logo ? (
            <img src={logo} alt={brandName} className="w-8 h-8 rounded-lg object-contain shrink-0" />
          ) : (
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold shrink-0"
              style={{ backgroundColor: primary }}
            >
              {brandName.slice(0, 2).toUpperCase()}
            </div>
          )}
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold truncate text-foreground">{brandName}</p>
              <p className="text-xs text-muted-foreground truncate">Espace partenaire</p>
            </div>
          )}
          <button
            onClick={() => setCollapsed(c => !c)}
            className="ml-auto p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-2 py-3 space-y-0.5 overflow-y-auto">
          {NAV_ITEMS.map(item => {
            const active = isActive(item.href);
            const badge = (item as any).badge;
            return (
              <PortailLink
                key={item.href}
                to={item.href}
                className={`flex items-center gap-3 px-2.5 py-2 rounded-lg text-sm transition-colors ${
                  active
                    ? "text-white font-medium"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                }`}
                style={active ? { backgroundColor: primary } : {}}
                title={collapsed ? item.label : undefined}
              >
                <div className="relative shrink-0">
                  <item.icon className="w-4 h-4" />
                  {badge > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 w-4 h-4 flex items-center justify-center rounded-full bg-red-500 text-white text-[9px] font-bold leading-none">
                      {badge > 9 ? "9+" : badge}
                    </span>
                  )}
                </div>
                {!collapsed && <span className="truncate flex-1">{item.label}</span>}
                {!collapsed && badge > 0 && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${active ? "bg-white/20 text-white" : "bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400"}`}>
                    {badge}
                  </span>
                )}
              </PortailLink>
            );
          })}
        </nav>

        {/* Bas sidebar : user + déconnexion */}
        <div className="border-t border-border/40 p-3 space-y-2">
          <PortailLink
            to="/parametres"
            className={`flex items-center gap-2.5 w-full px-2.5 py-2 rounded-lg text-sm transition-colors ${
              isActive("/parametres")
                ? "text-white font-medium"
                : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
            } ${collapsed ? "justify-center" : ""}`}
            style={isActive("/parametres") ? { backgroundColor: primary } : {}}
            title={collapsed ? "Paramètres" : undefined}
          >
            <Settings className="w-4 h-4 shrink-0" />
            {!collapsed && <span>Paramètres</span>}
          </PortailLink>
          <div className="border-t border-border/40 pt-2">
            <div className="flex items-center gap-2.5 min-w-0">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
                style={{ backgroundColor: primary }}
              >
                {portailUser?.avatarUrl ? (
                  <img src={portailUser.avatarUrl} alt="" className="w-8 h-8 rounded-full object-cover" />
                ) : (
                  getInitials(portailUser?.prenom ?? null, portailUser?.nom ?? null)
                )}
              </div>
              {!collapsed && (
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium truncate text-foreground">
                    {portailUser?.prenom} {portailUser?.nom}
                  </p>
                  <p className="text-xs text-muted-foreground truncate capitalize">
                    {portailUser?.role === "admin" ? "Administrateur" : "Contact"}
                  </p>
                </div>
              )}
            </div>
          </div>
          <button
            onClick={logout}
            className={`flex items-center gap-2.5 w-full px-2.5 py-2 rounded-lg text-sm text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors ${collapsed ? "justify-center" : ""}`}
            title={collapsed ? "Déconnexion" : undefined}
            data-testid="button-portail-logout"
          >
            <LogOut className="w-4 h-4 shrink-0" />
            {!collapsed && <span>Déconnexion</span>}
          </button>
        </div>
      </aside>

      {/* Contenu principal */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar avec cloche notifications + bouton feedback */}
        <div className="h-11 flex items-center justify-end gap-1 px-4 border-b border-border/40 bg-background/50 shrink-0">
          <button
            onClick={() => setFeedbackOpen(true)}
            className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
            title="Faire une suggestion"
            data-testid="btn-portail-feedback"
          >
            <Lightbulb className="w-4 h-4" />
          </button>
          <button
            onClick={() => setNotifPanelOpen(o => !o)}
            className="relative p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
            title="Notifications"
            data-testid="btn-notif-bell"
          >
            <Bell className="w-4 h-4" />
            {unreadNotifCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-4 h-4 flex items-center justify-center rounded-full bg-red-500 text-white text-[9px] font-bold leading-none">
                {unreadNotifCount > 9 ? "9+" : unreadNotifCount}
              </span>
            )}
          </button>
        </div>
        <PortailFeedbackModal isOpen={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
        <PortailNotificationsPanel
          isOpen={notifPanelOpen}
          onClose={() => setNotifPanelOpen(false)}
          onNotifRead={refreshNotifCount}
        />
        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
